import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { HeaderSection } from '@/components/shared/header-section'
import { PageContent } from '@/components/shared/page-content'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { DataTableToolbar } from '@/components/shared/data-table-manual/data-table-toolbar-manual'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { GET_SOFA_TYPES } from '@/components/shared/inputComponents/SofaFormTypeSelect'
import {
  GET_ALL_ERP_SHAPE_MAPPINGS,
  GET_ERP_SHAPE_MAPPINGS,
  GET_MAPPING_MANUFACTURERS,
} from './erpShapeMappingQueries'
import { columns, STATUS_BADGE } from './components/columns'
import ErpShapeMappingSheet from './components/ErpShapeMappingSheet'
import {
  isNonStandardMapping,
  type ErpShapeMappingRow,
  type ManufacturerOption,
} from './types'

const ALL = '__all__'
type QuickFilter = 'unmapped' | 'missing_image' | 'non_standard' | null
// Filters the backend can't do; these load the full list and filter here.
const isClientFilter = (q: QuickFilter) =>
  q === 'missing_image' || q === 'non_standard'

const manufacturerName = (m: ManufacturerOption) =>
  m.manufacturer_profiles.find(p => p?.name)?.name ?? `#${m.id}`

export default function ErpShapeMappings() {
  const [manufacturerId, setManufacturerId] = useState<number | null>(null)
  const [status, setStatus] = useState<string>(ALL)
  const [type, setType] = useState<string>(ALL)
  const [quick, setQuick] = useState<QuickFilter>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 })

  // Manufacturer ids differ per database, so default to Vilmers by name.
  const { data: manufacturersData } = useQuery(GET_MAPPING_MANUFACTURERS)
  const manufacturers: ManufacturerOption[] = useMemo(
    () =>
      [
        ...((manufacturersData?.findManyManufacturer ??
          []) as ManufacturerOption[]),
      ].sort((a, b) =>
        manufacturerName(a).localeCompare(manufacturerName(b))
      ),
    [manufacturersData]
  )
  useEffect(() => {
    if (manufacturerId !== null || manufacturers.length === 0) return
    const vilmers = manufacturers.find(m =>
      manufacturerName(m).toLowerCase().includes('vilmers')
    )
    setManufacturerId((vilmers ?? manufacturers[0]).id)
  }, [manufacturers, manufacturerId])

  const { data: typesData } = useQuery(GET_SOFA_TYPES)
  const typeNames: string[] = useMemo(
    () =>
      (typesData?.__type?.enumValues ?? [])
        .map((v: { name: string }) => v.name)
        .sort((a: string, b: string) => a.localeCompare(b)),
    [typesData]
  )

  const effectiveStatus =
    quick === 'unmapped' ? 'UNMAPPED' : status === ALL ? null : status
  const effectiveType = type === ALL ? null : type
  const search = searchTerm.trim() || null

  useEffect(() => {
    setPagination(p => ({ ...p, pageIndex: 0 }))
  }, [manufacturerId, status, type, quick, searchTerm])

  // Server-side page for the normal case.
  const paged = useQuery(GET_ERP_SHAPE_MAPPINGS, {
    variables: {
      manufacturerId,
      status: effectiveStatus,
      type: effectiveType,
      search,
      take: pagination.pageSize,
      skip: pagination.pageIndex * pagination.pageSize,
    },
    skip: manufacturerId === null || isClientFilter(quick),
    fetchPolicy: 'cache-and-network',
  })

  // The backend can't filter on "has image" or "standard type", so those quick
  // filters load the manufacturer's full list (a few hundred rows) and filter
  // here.
  const all = useQuery(GET_ALL_ERP_SHAPE_MAPPINGS, {
    variables: { manufacturerId },
    skip: manufacturerId === null || !isClientFilter(quick),
    fetchPolicy: 'cache-and-network',
  })

  const { rows, rowCount, loading } = useMemo(() => {
    if (!isClientFilter(quick)) {
      return {
        rows: (paged.data?.erpShapeMappings ?? []) as ErpShapeMappingRow[],
        rowCount: paged.data?.erpShapeMappingsCount ?? 0,
        loading: paged.loading,
      }
    }
    const term = search?.toLowerCase()
    const filtered = (
      (all.data?.erpShapeMappingsForImport ?? []) as ErpShapeMappingRow[]
    ).filter(
      (m: ErpShapeMappingRow) =>
        (quick === 'missing_image'
          ? !m.erp_image_url
          : isNonStandardMapping(m)) &&
        (!effectiveStatus || m.status === effectiveStatus) &&
        (!effectiveType || m.sofa_form_type === effectiveType) &&
        (!term ||
          m.erp_schematic_id.toLowerCase().includes(term) ||
          (m.notes ?? '').toLowerCase().includes(term))
    )
    const start = pagination.pageIndex * pagination.pageSize
    return {
      rows: filtered.slice(start, start + pagination.pageSize),
      rowCount: filtered.length,
      loading: all.loading,
    }
  }, [
    quick,
    paged.data,
    paged.loading,
    all.data,
    all.loading,
    search,
    effectiveStatus,
    effectiveType,
    pagination,
  ])

  const openSheet = useOpenDrawer(ErpShapeMappingSheet)

  const toggleQuick = (value: QuickFilter) =>
    setQuick(prev => (prev === value ? null : value))

  return (
    <PageContent>
      <div className="flex items-center justify-between">
        <HeaderSection
          title="ERP Shape Mappings"
          subtitle="Which of our sofa shapes each ERP module schematic imports as"
        />
        <Button
          onClick={() => openSheet({ manufacturerId })}
          disabled={manufacturerId === null}
        >
          Create New +
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={manufacturerId !== null ? String(manufacturerId) : undefined}
          onValueChange={v => setManufacturerId(Number(v))}
        >
          <SelectTrigger className="h-8 w-[180px]">
            <SelectValue placeholder="Manufacturer" />
          </SelectTrigger>
          <SelectContent>
            {manufacturers.map(m => (
              <SelectItem key={m.id} value={String(m.id)}>
                {manufacturerName(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={status}
          onValueChange={setStatus}
          disabled={quick === 'unmapped'}
        >
          <SelectTrigger className="h-8 w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {Object.entries(STATUS_BADGE).map(([value, { label }]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-8 w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value={ALL}>All shape types</SelectItem>
            {typeNames.map(name => (
              <SelectItem key={name} value={name} className="font-mono">
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(
          [
            ['unmapped', 'Unmapped'],
            ['missing_image', 'Missing image'],
            ['non_standard', 'Non-standard shape'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => toggleQuick(value)}
            className={cn(
              'h-8 rounded-full border px-3 text-xs transition-colors',
              quick === value
                ? 'border-primary bg-primary text-primary-foreground'
                : 'hover:bg-accent'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <DataTableManual
        data={rows}
        columns={columns}
        rowCount={rowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={() => {}}
        onColumnFiltersChange={() => {}}
        onRowClick={openSheet}
        isLoading={loading}
        emptyStateLabel="No mappings"
        toolbar={table => (
          <DataTableToolbar
            table={table}
            config={{
              search: {
                placeholder: 'Search schematic ID or notes...',
                accessor: 'erp_schematic_id',
                onSearch: setSearchTerm,
                onFilter: () => {},
              },
              columns: [],
            }}
          />
        )}
      />
    </PageContent>
  )
}
